import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PORTALS } from "../lib/portals";
import { PageHeader, Panel, StatCard, MatchRow, Empty, Spinner, Crest, StatusBadge, DemoTag } from "../components/kit";
import { Button } from "../components/ui/button";
import { MatchDayCountdown } from "../components/MatchDayCountdown";

const HeroMatch = ({ m, portal }) => {
  const nav = useNavigate();
  const isRef = portal === "referee";
  const target = isRef ? `/referee/matches/${m.id}/tactical` : portal === "coach" ? `/coach/tactics?match=${m.id}` : `/matches/${m.id}`;
  return (
    <div className="tro-card relative overflow-hidden p-5 md:p-7" data-testid="next-fixture-hero">
      <div className="absolute inset-0 tro-metal opacity-60" />
      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center">
        <div className="flex-1">
          <div className="flex items-center gap-2"><span className="tro-label">Next fixture</span><StatusBadge status={m.status} />{m.demo && <DemoTag />}</div>
          <div className="mt-3 flex items-center gap-4">
            <Crest club={m.home} size={56} />
            <div className="text-xl font-extrabold md:text-3xl">{m.home.name} <span className="font-mono text-[#6C757D]">{m.score.home}–{m.score.away}</span> {m.away.name}</div>
            <Crest club={m.away} size={56} />
          </div>
          <div className="mt-2 text-sm text-[#343A40]">{m.competition?.name} · {m.match_number} · {m.venue} · {fmtDate(m.kickoff)}</div>
        </div>
        <Button data-testid={isRef ? "enter-match-room-btn" : "open-fixture-btn"} onClick={() => nav(target)} className="h-14 px-7 text-base font-extrabold uppercase tracking-wide">
          {isRef ? "Enter Match Room" : portal === "coach" ? "Open Tactical Board" : "Open Match"} <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default function PortalDashboard({ portal }) {
  const { me } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/dashboard/${portal}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [portal]);
  if (err) return <Empty text={err} />;
  if (!d) return <Spinner />;
  const pending = me.memberships.filter((m) => m.status === "pending");
  const next = d.upcoming[0];
  return (
    <div className="space-y-6" data-testid={`${portal}-dashboard`}>
      <PageHeader title={`TRO ${PORTALS[portal].label} Portal`} sub={`Welcome back, ${me.user.name}`} />
      {pending.length > 0 && <div className="rounded-lg border border-[#FFD400] bg-[#FFD400]/10 p-3 text-sm font-semibold" data-testid="pending-roles-banner">Pending approval: {pending.map((m) => m.role.replace(/_/g, " ")).join(", ")}</div>}
      {next && ["referee", "coach", "officials", "commissioner", "player", "club"].includes(portal) && <HeroMatch m={next} portal={portal} />}
      {d.next_prematch && <MatchDayCountdown match={d.next_prematch} />}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6 tro-stagger">
        {d.cards.map((c) => <StatCard key={c.label} label={c.label} value={c.value} testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />)}
      </div>
      <div className="grid gap-6 xl:grid-cols-3">
        <Panel title="Upcoming & live matches" className="min-w-0 xl:col-span-2" testid="upcoming-matches">
          {d.upcoming.length ? <div className="space-y-2">{d.upcoming.map((m) => <MatchRow key={m.id} m={m} to={portal === "referee" ? `/referee/matches/${m.id}/tactical` : undefined} />)}</div> : <Empty text="No upcoming matches." />}
        </Panel>
        <div className="min-w-0 space-y-6">
          <Panel title="Pending tasks" testid="pending-tasks">
            {d.tasks.length ? <ul className="space-y-2">{d.tasks.map((t) => <li key={t} className="flex gap-2 text-sm"><CheckCircle2 className="h-4 w-4 shrink-0 text-[#6C757D]" />{t}</li>)}</ul> : <Empty text="All caught up." />}
          </Panel>
          <Panel title="Recent activity" action={<Link to="/notifications" className="text-xs font-bold hover:underline">All</Link>} testid="recent-activity">
            {d.activity.length ? <ul className="space-y-2">{d.activity.map((a) => <li key={a.id} className="text-sm"><b>{a.title}</b><div className="text-xs text-[#6C757D]">{a.body} · {fmtDate(a.created_at)}</div></li>)}</ul> : <Empty text="No recent activity." />}
          </Panel>
        </div>
      </div>
    </div>
  );
}
